<template>
  <div class="ann-overlay" :style="{ width: width + 'px' }">
    <img :src="imageUrl" class="ann-overlay-img" draggable="false" alt="" />
    <svg class="ann-overlay-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
      <template v-for="a in annotations">
        <rect
          v-if="a.tool === 'rect'"
          :key="a.id"
          :x="a.points[0] * 100"
          :y="a.points[1] * 100"
          :width="(a.points[2] - a.points[0]) * 100"
          :height="(a.points[3] - a.points[1]) * 100"
          fill="none"
          :stroke="a.color"
          stroke-width="2"
          vector-effect="non-scaling-stroke"
        />
        <g v-else-if="a.tool === 'arrow'" :key="a.id">
          <line
            :x1="a.points[0] * 100"
            :y1="a.points[1] * 100"
            :x2="a.points[2] * 100"
            :y2="a.points[3] * 100"
            :stroke="a.color"
            stroke-width="2"
            vector-effect="non-scaling-stroke"
          />
          <polygon :points="arrowHeadPoints(a)" :fill="a.color" />
        </g>
      </template>
    </svg>
    <span
      v-for="a in textAnns"
      :key="a.id"
      class="ann-text"
      :style="{ left: a.points[0] * 100 + '%', top: a.points[1] * 100 + '%', color: a.color }"
      >{{ a.text }}</span
    >
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import type { ImageAnnotation } from './types';

export default Vue.extend({
  name: 'AnnotationOverlay',
  props: {
    imageUrl: { type: String, required: true },
    annotations: { type: Array as () => ImageAnnotation[], default: () => [] },
    width: { type: Number, default: 400 },
  },
  computed: {
    textAnns(): ImageAnnotation[] {
      return this.annotations.filter((annotation) => annotation.tool === 'text');
    },
  },
  methods: {
    arrowHeadPoints(annotation: ImageAnnotation): string {
      const [x1 = 0, y1 = 0, x2 = 0, y2 = 0] = annotation.points;
      const angle = Math.atan2(y1 - y2, x1 - x2);
      const tip = { x: x2 + Math.cos(angle) * 0.04, y: y2 + Math.sin(angle) * 0.04 };
      const left = {
        x: x2 + Math.cos(angle + Math.PI / 2) * 0.024,
        y: y2 + Math.sin(angle + Math.PI / 2) * 0.024,
      };
      const right = {
        x: x2 + Math.cos(angle - Math.PI / 2) * 0.024,
        y: y2 + Math.sin(angle - Math.PI / 2) * 0.024,
      };
      return [tip, left, right]
        .map((point) => `${(point.x * 100).toFixed(2)},${(point.y * 100).toFixed(2)}`)
        .join(' ');
    },
  },
});
</script>

<style lang="scss" scoped>
.ann-overlay {
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: $border-radius-sm;
}

.ann-overlay-img {
  display: block;
  width: 100%;
  height: auto;
}

.ann-overlay-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.ann-text {
  position: absolute;
  padding: 1px 5px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  transform: translate(4px, 4px);
  pointer-events: none;
}
</style>
